// src/components/StudentSidebar.js
// Student Portal navigation sidebar

import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Drawer,
    Typography,
    Divider,
    Collapse,
    IconButton,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import FolderIcon from '@mui/icons-material/Folder';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../utils/axiosInstance';
import { useCompanySettings } from '../contexts/CompanySettingsContext';
import logoFinal from '../assets/images/logo-final.png';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const collapsedWidth = 60;

// Student Portal uses purple accent color
const colors = {
    sidebarBg: '#0f172a',
    sidebarBgSecondary: '#1e293b',
    sidebarHover: 'rgba(139, 92, 246, 0.08)',
    sidebarText: '#e2e8f0',
    sidebarTextMuted: '#94a3b8',
    sidebarTextActive: '#ffffff',
    accent: '#8b5cf6',  // Purple for student portal
    accentLight: 'rgba(139, 92, 246, 0.15)',
    accentGlow: 'rgba(139, 92, 246, 0.4)',
    border: 'rgba(255, 255, 255, 0.06)',
};

export default function StudentSidebar({ drawerWidth, isOpen, onClose, isMobile, toggleSidebar }) {
    const location = useLocation();

    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openSections, setOpenSections] = useState({});
    const { companyLogo } = useCompanySettings();

    // Determine if sidebar is collapsed (not open and not mobile)
    const isCollapsed = !isOpen && !isMobile;
    const currentWidth = isCollapsed ? collapsedWidth : drawerWidth;

    const getIconComponent = (iconName) => {
        if (!iconName) return FolderIcon;
        const IconComponent = MuiIcons[iconName];
        return IconComponent || FolderIcon;
    };

    useEffect(() => {
        if (!loading) return;
        const fetchModules = async () => {
            try {
                // Fetch only Student Portal modules
                const response = await axiosInstance.get('/api/modules?panel=student');

                if (response.data.success) {
                    const parsed = JSON.parse(response.data.data);
                    setModules(parsed.modules);

                    // Initialize all sections as closed
                    const initialSections = {};
                    parsed.modules.forEach((module) => {
                        initialSections[module.slug] = false;
                    });
                    setOpenSections(initialSections);
                }
            } catch (err) {
                console.error('Error fetching student modules:', err);
                setError('Failed to load navigation');
            } finally {
                setLoading(false);
            }
        };

        fetchModules();
    }, [loading]);

    // Auto-open parent section when child route is active
    useEffect(() => {
        if (modules.length === 0) return;

        const currentPath = location.pathname;

        modules.forEach(module => {
            const hasSubmodules = module.submodules && module.submodules.length > 0;

            if (hasSubmodules) {
                const isChildActive = module.submodules.some(sub => {
                    const subPath = `/${sub.route}`;
                    return currentPath === subPath || currentPath.startsWith(subPath + '/');
                });

                if (isChildActive) {
                    setOpenSections(prev => ({ ...prev, [module.slug]: true }));
                }
            } else if (module.route) {
                const modulePath = `/${module.route}`;
                const isActive = currentPath === modulePath || currentPath.startsWith(modulePath + '/');

                if (isActive) {
                    setOpenSections(prev => ({ ...prev, [module.slug]: true }));
                }
            }
        });
    }, [location.pathname, modules]);

    const handleSectionToggle = (section) => {
        if (isCollapsed) return;
        setOpenSections(prevState => ({
            ...prevState,
            [section]: !prevState[section],
        }));
    };

    const handleDrawerToggle = () => {
        if (isMobile && onClose) {
            onClose();
        }
    };

    const isPathActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const renderModuleItem = (module) => {
        if (!module.route) return null;

        const fullPath = `/${module.route}`;
        const IconComponent = getIconComponent(module.icon);
        const isActive = isPathActive(fullPath);

        const itemContent = (
            <ListItem key={module.id} disablePadding sx={{ position: 'relative' }}>
                {isActive && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: '60%',
                            bgcolor: colors.accent,
                            borderRadius: '0 4px 4px 0',
                            boxShadow: `0 0 8px ${colors.accentGlow}`,
                        }}
                    />
                )}
                <ListItemButton
                    component={NavLink}
                    to={fullPath}
                    onClick={handleDrawerToggle}
                    sx={{
                        pl: isCollapsed ? 0 : 3,
                        pr: isCollapsed ? 0 : undefined,
                        py: 1,
                        mx: isCollapsed ? 0.5 : 1.5,
                        borderRadius: '8px',
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                        color: isActive ? colors.sidebarTextActive : colors.sidebarText,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        bgcolor: isActive ? colors.accentLight : 'transparent',
                        '&:hover': {
                            bgcolor: isActive ? colors.accentLight : colors.sidebarHover,
                            transform: !isCollapsed ? 'translateX(2px)' : 'none',
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: isCollapsed ? 'auto' : 32,
                            color: isActive ? colors.accent : 'inherit',
                            transition: 'color 0.2s ease',
                        }}
                    >
                        <IconComponent sx={{ fontSize: '1.1rem' }} />
                    </ListItemIcon>
                    {!isCollapsed && (
                        <ListItemText
                            primary={module.name}
                            primaryTypographyProps={{
                                sx: {
                                    fontFamily,
                                    fontSize: '0.8125rem',
                                    fontWeight: isActive ? 600 : 500,
                                    letterSpacing: '-0.01em',
                                }
                            }}
                        />
                    )}
                </ListItemButton>
            </ListItem>
        );

        if (isCollapsed) {
            return (
                <Tooltip key={module.id} title={module.name} placement="right" arrow>
                    {itemContent}
                </Tooltip>
            );
        }

        return itemContent;
    };

    const renderModuleSection = (module) => {
        const hasSubmodules = module.submodules && module.submodules.length > 0;
        const visibleSubmodules = hasSubmodules ? module.submodules : [];

        if (hasSubmodules && visibleSubmodules.length === 0) {
            return null;
        }

        const isOpen = openSections[module.slug];

        if (isCollapsed) {
            return (
                <React.Fragment key={module.id}>
                    <Divider sx={{ borderColor: colors.border, mx: 1.5, my: 1 }} />
                    {hasSubmodules
                        ? visibleSubmodules.map(submodule => renderModuleItem(submodule))
                        : renderModuleItem(module)
                    }
                </React.Fragment>
            );
        }

        return (
            <React.Fragment key={module.id}>
                <ListItem disablePadding sx={{ mt: 1.5 }}>
                    <ListItemButton
                        onClick={() => handleSectionToggle(module.slug)}
                        sx={{
                            py: 0.75,
                            px: 2,
                            '&:hover': { bgcolor: 'transparent' },
                        }}
                    >
                        <Typography sx={{
                            fontFamily,
                            fontSize: '0.725rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: colors.sidebarTextMuted,
                            flex: 1,
                        }}>
                            {module.name}
                        </Typography>
                        <IconButton
                            size="small"
                            sx={{
                                color: colors.sidebarTextMuted,
                                p: 0.25,
                                transition: 'transform 0.2s ease',
                                transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                            }}
                        >
                            <ExpandLess sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </ListItemButton>
                </ListItem>
                <Collapse in={isOpen} timeout={200} unmountOnExit>
                    <List component="div" disablePadding sx={{ pb: 0.5 }}>
                        {hasSubmodules
                            ? visibleSubmodules.map(submodule => renderModuleItem(submodule))
                            : renderModuleItem(module)
                        }
                    </List>
                </Collapse>
            </React.Fragment>
        );
    };

    const drawerVariant = isMobile ? 'temporary' : 'permanent';
    const shouldShowDrawer = isMobile ? isOpen : true;

    return (
        <Drawer
            sx={{
                '& .MuiDrawer-paper': {
                    width: currentWidth,
                    boxSizing: 'border-box',
                    background: `linear-gradient(180deg, ${colors.sidebarBg} 0%, ${colors.sidebarBgSecondary} 100%)`,
                    color: colors.sidebarText,
                    borderRight: 'none',
                    top: { xs: 56, sm: 64 },
                    height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    transition: 'width 0.3s ease',
                    '&::-webkit-scrollbar': { width: '3px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '4px',
                        '&:hover': { background: 'rgba(255,255,255,0.2)' },
                    },
                },
            }}
            variant={drawerVariant}
            anchor="left"
            open={shouldShowDrawer}
            onClose={onClose}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Logo Header with Student Portal indicator */}
                <Box sx={{
                    px: isCollapsed ? 0.5 : 2,
                    py: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isCollapsed ? 'center' : 'space-between',
                    borderBottom: `1px solid ${colors.border}`,
                    overflow: 'visible',
                }}>
                    {isCollapsed ? (
                        <IconButton
                            onClick={toggleSidebar}
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '8px',
                                bgcolor: colors.accent,
                                color: '#fff',
                                '&:hover': {
                                    bgcolor: colors.accent,
                                    opacity: 0.9,
                                },
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <img
                                    src={companyLogo || logoFinal}
                                    alt="Logo"
                                    style={{
                                        height: 35,
                                        width: 'auto',
                                        borderRadius: 0,
                                    }}
                                    onError={(e) => { e.target.onerror = null; e.target.src = logoFinal; }}
                                />
                                <Typography
                                    sx={{
                                        fontFamily,
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        color: colors.accent,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    Student
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={toggleSidebar}
                                size="small"
                                sx={{ color: colors.sidebarTextMuted }}
                            >
                                <MenuOpenIcon />
                            </IconButton>
                        </>
                    )}
                </Box>

                {/* Navigation */}
                <List component="nav" sx={{ flexGrow: 1, pt: 1, pb: 2 }}>

                    {/* Loading State */}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={20} sx={{ color: colors.accent }} />
                        </Box>
                    )}

                    {/* Error State */}
                    {!loading && error && (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#ef4444', mb: 1 }}>
                                {error}
                            </Typography>
                            <Typography
                                onClick={() => { setError(null); setLoading(true); }}
                                sx={{ fontFamily, fontSize: '0.7rem', color: colors.accent, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            >
                                Retry
                            </Typography>
                        </Box>
                    )}

                    {/* Dynamic Modules */}
                    {!loading && !error && modules.map(module => renderModuleSection(module))}
                </List>
            </Box>
        </Drawer>
    );
}

export { collapsedWidth };
